import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { format, parseISO } from 'date-fns';
import useMode from '../../hooks/useMode';
import { Box, Flex, Text } from 'rebass';
import BackflowTestReport from './BackflowTestReport';
import BackflowTestAssembly from './assembly/BackflowTestAssembly';
import { useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import FilePreviews from '../File/FilePreviews';
import { gql } from '@/modes/waterSupplier/gql';
import { AssemblyInfo, BackflowTest, Job } from './queries';

const GET_COMPANY = gql(`
  query TestCompanyByName($where: JSON, $limit: Int) {
    vendors(where: $where, limit: $limit) {
      id
    }
  }
`);

type BackflowTestProps = {
  backflowTest: BackflowTest;
  job: Job;
  formAssembly?: AssemblyInfo;
};

const BackflowTestDetail: React.FC<BackflowTestProps> = ({
  backflowTest,
  job,
  formAssembly,
}) => {
  const history = useHistory();
  const mode = useMode();
  const apollo = useApolloClient();

  const { backflowAssembly } = backflowTest;

  const { formData } = job;

  const originalBackflowAssembly = backflowTest.originalBackflowAssembly || backflowAssembly || undefined;

  const waterService = backflowAssembly?.waterService;
  const serviceAddress = backflowAssembly?.waterService?.serviceAddresses?.[0];
  const customer = serviceAddress?.customer;

  const differentName =
    job.siteContact &&
    customer &&
    customer.profile.name?.toLowerCase() !== job.siteContact.name.toLowerCase();

  const differentAddress =
    job.siteContact &&
    job.siteContact.address &&
    job.address.id !== job.siteContact.address.id;

  return (
    <>
      <Flex flexDirection={['column', 'column', 'row']}>
        <Box width={['100%', '100%', '50%']} flexGrow={1} flexShrink={1}>
          <Accordion title="Service Address">
            {waterService && (
              <>
                <AccordionItem
                  kind="link"
                  label="Service"
                  value={waterService.siteNumber}
                  href={`/${mode}/water-services/${waterService.id}`}
                />
                {customer && (
                  <>
                    <AccordionItem
                      kind="link"
                      label="Customer"
                      value={customer.profile.name}
                      href={`/${mode}/customers/${customer.id}`}
                    />
                  </>
                )}
              </>
            )}
            <AccordionItem
              label="Address"
              value={<Address address={job.address} />}
            />
          </Accordion>

          {job.siteContact && (differentName || differentAddress) && (
            <Accordion
              title={
                mode === 'water-supplier' ? "Tester's Customer" : 'Customer'
              }
            >
              {differentName && (
                <AccordionItem label="Name" value={job.siteContact.name} />
              )}
              {job.siteContact.phone && (
                <AccordionItem label="Phone" value={job.siteContact.phone} />
              )}
              {job.siteContact.email && (
                <AccordionItem label="Email" value={job.siteContact.email} />
              )}
              {job.siteContact.address && differentAddress && (
                <AccordionItem
                  label="Address"
                  value={<Address address={job.siteContact.address} />}
                />
              )}
            </Accordion>
          )}

          <Accordion title="Tester">
            {backflowTest.testingCompanyName && (
              <AccordionItem
                kind="link"
                label="Company"
                value={backflowTest.testingCompanyName}
                onClick={async () => {
                  const result = await apollo.query<{
                    vendors: { id: string }[];
                  }>({
                    query: GET_COMPANY,
                    variables: {
                      where: {
                        'profile.name': backflowTest.testingCompanyName,
                      },
                      limit: 1,
                    },
                  });
                  if (result?.data?.vendors?.[0]?.id) {
                    history.push(
                      `/${mode}/testers/${result.data.vendors[0].id}`
                    );
                  }
                }}
              />
            )}
            {backflowTest.testerName && (
              <AccordionItem label="Name" value={backflowTest.testerName} />
            )}
            <AccordionItem label="Gauge" value={formData.gaugeNumber} />
            {formData.gaugeCalibrated && (
              <AccordionItem
                label="Gauge Calibrated"
                value={format(parseISO(formData.gaugeCalibrated), 'M/d/yyyy')}
              />
            )}
          </Accordion>

          <BackflowTestAssembly
            key={job.id}
            originalBackflowAssembly={originalBackflowAssembly}
            backflowAssemblyId={backflowAssembly?.id}
            formAssembly={formAssembly}
            formData={formData}
          />
        </Box>
        <Box width={['100%', '100%', '50%']} flexGrow={1} flexShrink={1}>
          <BackflowTestReport
            key={job.id}
            formData={formData}
            assemblyType={formAssembly?.type ?? undefined}
            rpCheck2Type={backflowTest.waterSupplier?.rpCheck2Type ?? 'USC10'}
          />
          {(job.attachments.length > 0 || !!formData?.comments) && (
            <Accordion title="Photos and Comments">
              <FilePreviews files={job.attachments as any} />

              {!!formData?.comments && (
                <>
                  <AccordionItem label="Comments" value=" " />
                  <Text variant="body2">{formData.comments}</Text>
                </>
              )}
            </Accordion>
          )}
        </Box>
      </Flex>
    </>
  );
};

BackflowTestDetail.propTypes = {
  backflowTest: PropTypes.any,
};

export default BackflowTestDetail;
